import type { JsonObject, JsonValue } from "@/lib/json";

export type { JsonObject, JsonPrimitive, JsonValue } from "@/lib/json";

export const MAX_CORE_SELLING_POINTS = 10;

export interface CreativeInput {
  caption: string;
  duration_sec: number;
  visual_style: string;
  music_mood: string;
  has_celeb: boolean;
}

export interface PredictionRequest {
  creative: CreativeInput;
  project_name?: string;
  campaign_objective?: string;
  core_selling_points?: string[];
  source?: "llm" | "mock" | null;
  platform_budgets?: Record<PlatformId, number>;
  total_budget: number;
  platform_alloc: Record<string, number>;
  audience_age_buckets?: number[] | null;
  /** 数组是 V2 契约；number 仅用于兼容迁移前的本机历史记录。 */
  audience_gender?: number[] | number | null;
  audience_city_tiers?: number[] | null;
  kol_niche?: string | null;
  use_llm: boolean;
  llm_calibrate: boolean;
  n_souls: number;
  lifecycle_days: number;
  today?: string | null;
  daypart: string;
  weather_temp_c: number;
  rainy: boolean;
  sentiment: string;
  cross_platform_overlap: number;
  enable_crossplat: boolean;
  enable_discourse: boolean;
  discourse_n_comments: number;
  enable_brand_memory: boolean;
  brand_memory_days?: number;
  enable_recsys_rl: boolean;
  enable_groupchat: boolean;
  groupchat_n_agents: number;
  groupchat_n_rounds: number;
  groupchat_stance_overrides?: Record<string, number> | null;
  own_brand?: string | null;
  category?: string | null;
  competitors?: string[] | null;
  target_niches?: string[] | null;
  enable_kol_ilp: boolean;
  enable_search_elasticity: boolean;
  season?: string | null;
  holiday_context?: string | null;
  upcoming_holiday?: string | null;
  weather_desc?: string | null;
  sentiment_reason?: string | null;
  world_news_summary?: string[] | null;
  competitor_moves?: string[] | null;
  social_trends?: string[] | null;
  policy_regulation?: string[] | null;
}

export interface WorldContext {
  temp_c: number | null;
  weather_desc?: string;
  season?: string;
  holiday_context?: string;
  upcoming_holiday?: string;
  sentiment?: string | null;
  sentiment_reason?: string;
  world_news_summary?: string[];
  news?: string[];
  competitor_moves?: string[];
  social_trends?: string[];
  policy_regulation?: string[];
  _source?: string;
}

export interface PredictionKpis {
  ctr?: number;
  cvr?: number;
  roi?: number;
  impressions?: number;
  total_impressions?: number;
  total_reach?: number;
  clicks?: number;
  conversions?: number;
  cost?: number;
  cost_cny?: number;
  revenue?: number;
  revenue_cny?: number;
  intent_high?: number;
  [key: string]: number | undefined;
}

export interface PlatformPrediction {
  ctr?: number;
  cvr?: number;
  roi?: number;
  ctr_std?: number;
  cvr_std?: number;
  roi_std?: number;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  cost?: number;
  revenue?: number;
  [key: string]: JsonValue | undefined;
}

export interface LifecyclePrediction {
  day_axis?: number[];
  days?: number[];
  paid?: number[];
  organic?: number[];
  total?: number[];
  paid_daily?: number[];
  organic_daily?: number[];
  total_daily?: number[];
  peak_day?: number;
  organic_share?: number;
  branching_ratio?: number;
  [key: string]: JsonValue | undefined;
}

export interface SoulQuote {
  persona_id?: number | string;
  persona?: string;
  persona_summary?: string;
  persona_oneliner?: string;
  quote?: string;
  reason?: string;
  verdict?: "loyalist" | "clicked" | "watching" | "skipped" | string;
  will_click?: boolean;
  purchase_intent_7d?: number;
  feel?: string;
  source?: string;
  [key: string]: JsonValue | undefined;
}

export interface PredictedSentiment {
  sentiment_distribution?: {
    positive?: number | null;
    neutral?: number | null;
    negative?: number | null;
  };
  positive?: number;
  neutral?: number;
  negative?: number;
  net_sentiment_score?: number | null;
  high_intent_pct?: number | null;
  avg_purchase_intent_7d?: number | null;
  source?: string;
  status?: string;
  valid_sentiment_count?: number;
  valid_intent_count?: number;
  unavailable_reason?: string | null;
  feel_breakdown?: Record<string, number>;
  key_opinion_themes?: JsonValue[];
  agent_count?: number;
  llm_backed?: number;
}

export const PRODUCT_ARTIFACT_MODULES = [
  "virtual_user_profile",
  "public_opinion_simulation",
  "diffusion_conversion",
  "purchase_intent_profile",
  "report_overview",
  "market_insights",
  "distribution_strategy",
  "supplemental_analysis",
] as const;

export type ProductArtifactModule =
  (typeof PRODUCT_ARTIFACT_MODULES)[number];

export type ProductModules = Partial<
  Record<ProductArtifactModule, JsonObject>
>;

export interface PredictionPayload {
  session_id?: string;
  kpis?: PredictionKpis;
  per_platform?: Record<string, PlatformPrediction>;
  lifecycle?: LifecyclePrediction;
  soul_quotes?: SoulQuote[];
  predicted_sentiment?: PredictedSentiment;
  macro?: JsonObject;
  schema_outputs?: Record<string, JsonValue>;
  extras?: Record<string, JsonValue>;
  dag?: JsonObject;
  mc_stats?: Record<string, number>;
  report?: JsonValue;
  product_modules?: ProductModules;
  [key: string]:
    | JsonValue
    | PredictionKpis
    | Record<string, PlatformPrediction>
    | LifecyclePrediction
    | SoulQuote[]
    | PredictedSentiment
    | ProductModules
    | undefined;
}

export interface PhaseInsight {
  [key: string]: JsonValue;
}

export interface PhaseEvent {
  type: "phase";
  name: string;
  status: "start" | "done";
  ms?: number;
  total?: number;
  insight?: PhaseInsight;
}

export interface SoulVoteEvent {
  type: "soul_vote";
  done: number;
  total?: number;
  persona?: string;
  quote?: string;
  verdict?: string;
  persona_id?: number | string;
}

export interface FinalEvent {
  type: "final";
  payload: PredictionPayload;
}

export interface ErrorEvent {
  type: "error";
  message?: string;
}

export interface KeepaliveEvent {
  type: "keepalive";
}

export interface WorkflowStepEvent {
  type: "workflow_step";
  step:
    | "task_creation"
    | "simulation_calibration"
    | "simulation_process"
    | "report_generation";
  order: number;
  status: "start" | "done" | "failed";
}

export interface ArtifactEvent {
  type: "artifact";
  module: ProductArtifactModule;
  revision: number;
  payload: JsonObject;
}

export type PredictionStreamEvent =
  | PhaseEvent
  | SoulVoteEvent
  | FinalEvent
  | ErrorEvent
  | KeepaliveEvent
  | WorkflowStepEvent
  | ArtifactEvent;

export interface CannedPredictionEvent {
  t_ms: number;
  event: PredictionStreamEvent;
}

export interface CannedPrediction {
  recorded_at?: string;
  caption?: string;
  source?: string;
  n_events?: number;
  duration_ms?: number;
  events: CannedPredictionEvent[];
}

export const PLATFORM_IDS = [
  "douyin",
  "xhs",
  "wechat_video",
  "bilibili",
  "kuaishou",
  "tiktok",
  "instagram",
  "youtube",
] as const;

export type PlatformId = (typeof PLATFORM_IDS)[number];

export const DEFAULT_PLATFORM_BUDGETS: Record<PlatformId, number> = {
  douyin: 30_000,
  xhs: 20_000,
  wechat_video: 0,
  bilibili: 0,
  kuaishou: 0,
  tiktok: 0,
  instagram: 0,
  youtube: 0,
};
